import React, { useState, useEffect } from 'react';
import { 
  Filter, 
  Plus, 
  X, 
  Search,
  Loader2,
  CheckCircle,
  Star,
  ExternalLink,
  FileText,
  Users,
  FileText as FileTextIcon,
  Edit2
} from 'lucide-react';

const CategoriesPage = () => {
  const [activeTab, setActiveTab] = useState('documents'); // 'documents' or 'people'
  
  // Documents tab state
  const [documents, setDocuments] = useState([]);
  const [filteredDocuments, setFilteredDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMainCategory, setSelectedMainCategory] = useState('');
  const [selectedSubCategory, setSelectedSubCategory] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddCategoryModal, setShowAddCategoryModal] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [availableCategories, setAvailableCategories] = useState({});
  const [selectedCategoriesToAdd, setSelectedCategoriesToAdd] = useState([]);
  const [addingCategories, setAddingCategories] = useState(false);
  
  // People tab state
  const [experts, setExperts] = useState({ pending: [], completed: [] });
  const [selectedExpert, setSelectedExpert] = useState(null);
  const [editingExpert, setEditingExpert] = useState(null); // Expert being edited (from completed section)
  const [selectedMainCategories, setSelectedMainCategories] = useState([]);
  const [selectedSubCategories, setSelectedSubCategories] = useState([]);
  const [savingExpert, setSavingExpert] = useState(false);
  const [loadingExperts, setLoadingExperts] = useState(true);
  
  // Add New Category state
  const [showAddCategoryForm, setShowAddCategoryForm] = useState(false);
  const [newMainCategory, setNewMainCategory] = useState('');
  const [newSubCategories, setNewSubCategories] = useState(['']);
  const [savingCategory, setSavingCategory] = useState(false);

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8001';
  const SUPABASE_STORAGE_BASE = 'https://gvzakaegbycuawdvigap.supabase.co/storage/v1/object/public/pdfs';

  // Build PDF URL from storage_path
  const getPdfUrl = (storagePath) => {
    if (!storagePath) return null;
    // Remove 'pdfs/' prefix if present
    let path = storagePath.replace(/^pdfs\//, '');
    // Encode each path segment separately to preserve slashes
    const encodedPath = path.split('/').map(segment => encodeURIComponent(segment)).join('/');
    return `${SUPABASE_STORAGE_BASE}/${encodedPath}`;
  };

  // Fetch all documents with their categories (batch fetch)
  const fetchDocuments = async () => {
    try {
      setLoading(true);
      // Use include_categories parameter to get all categories in one request
      const response = await fetch(`${API_URL}/api/v5/documents?limit=1000&include_categories=true`);
      const data = await response.json();
      const docs = data.documents || [];
      
      // Categories are already included in the response
      // Debug: Check how many have categories
      const withCategories = docs.filter(d => d.categories && d.categories.length > 0);
      console.log(`Fetched ${docs.length} documents, ${withCategories.length} have categories`);
      
      setDocuments(docs);
      setFilteredDocuments(docs);
    } catch (error) {
      console.error('Error fetching documents:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch all available categories
  const fetchAvailableCategories = async () => {
    try {
      const response = await fetch(`${API_URL}/api/v6/categories`);
      const data = await response.json();
      if (data.success) {
        setAvailableCategories(data.categories || {});
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  useEffect(() => {
    if (activeTab === 'documents') {
      fetchDocuments();
      fetchAvailableCategories();
    } else if (activeTab === 'people') {
      fetchExperts();
      fetchAvailableCategories();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);
  
  const fetchExperts = async () => {
    try {
      setLoadingExperts(true);
      const response = await fetch(`${API_URL}/api/v6/experts`);
      const data = await response.json();
      
      if (data.success) {
        setExperts({
          pending: data.pending || [],
          completed: data.completed || []
        });
        
        // If no experts found, try loading from CSV
        if (data.total === 0) {
          try {
            await fetch(`${API_URL}/api/v6/experts/load-csv`, { method: 'POST' });
            // Refetch after loading
            const refreshResponse = await fetch(`${API_URL}/api/v6/experts`);
            const refreshData = await refreshResponse.json();
            if (refreshData.success) {
              setExperts({
                pending: refreshData.pending || [],
                completed: refreshData.completed || []
              });
            }
          } catch (loadError) {
            console.error('Error loading experts from CSV:', loadError);
          }
        }
      }
    } catch (error) {
      console.error('Error fetching experts:', error);
    } finally {
      setLoadingExperts(false);
    }
  };
  
  const handleExpertSelect = (expertName) => {
    setSelectedExpert(expertName);
    setEditingExpert(null);
    setSelectedMainCategories([]);
    setSelectedSubCategories([]);
  };
  
  const handleEditExpert = (expert) => {
    setEditingExpert(expert);
    setSelectedExpert(expert.name);
    // Load existing categories for this expert
    const mainCats = new Set();
    const subCats = [];
    
    if (expert.categories && expert.categories.length > 0) {
      expert.categories.forEach(cat => {
        mainCats.add(cat.main_category);
        if (!cat.is_main_category_only) {
          subCats.push(`${cat.main_category} > ${cat.sub_category}`);
        }
      });
    }
    
    setSelectedMainCategories(Array.from(mainCats));
    setSelectedSubCategories(subCats);
  };
  
  const handleCancelEdit = () => {
    setEditingExpert(null);
    setSelectedExpert(null);
    setSelectedMainCategories([]);
    setSelectedSubCategories([]);
  };
  
  const handleSaveNewCategory = async () => {
    if (!newMainCategory.trim()) {
      alert('Please enter a main category name');
      return;
    }
    
    const validSubCats = newSubCategories.filter(sc => sc.trim());
    if (validSubCats.length === 0) {
      alert('Please enter at least one sub-category');
      return;
    }
    
    try {
      setSavingCategory(true);
      
      const response = await fetch(`${API_URL}/api/v6/categories`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          main_category: newMainCategory.trim(),
          sub_categories: validSubCats.map(sc => sc.trim())
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        // Refresh categories
        await fetchAvailableCategories();
        // Reset form
        setShowAddCategoryForm(false);
        setNewMainCategory('');
        setNewSubCategories(['']);
        alert('Category created successfully!');
      } else {
        alert('Failed to create category');
      }
    } catch (error) {
      console.error('Error creating category:', error);
      alert('Error creating category');
    } finally {
      setSavingCategory(false);
    }
  };
  
  const handleMainCategoryToggle = (mainCategory) => {
    setSelectedMainCategories(prev => {
      if (prev.includes(mainCategory)) {
        // Remove main category and all its sub-categories
        const subCatsToRemove = availableCategories[mainCategory]?.map(sc => `${mainCategory} > ${sc.sub_category}`) || [];
        setSelectedSubCategories(prevSub => prevSub.filter(sc => !subCatsToRemove.includes(sc)));
        return prev.filter(mc => mc !== mainCategory);
      } else {
        return [...prev, mainCategory];
      }
    });
  };
  
  const handleSubCategoryToggle = (mainCategory, subCategory) => {
    const key = `${mainCategory} > ${subCategory}`;
    setSelectedSubCategories(prev => {
      if (prev.includes(key)) {
        return prev.filter(k => k !== key);
      } else {
        return [...prev, key];
      }
    });
  };
  
  const handleSelectAllSubCategories = (mainCategory) => {
    const subCats = availableCategories[mainCategory] || [];
    const keys = subCats.map(sc => `${mainCategory} > ${sc.sub_category}`);
    setSelectedSubCategories(prev => {
      const newKeys = keys.filter(k => !prev.includes(k));
      return [...prev, ...newKeys];
    });
  };
  
  const handleSaveExpert = async () => {
    const expertName = selectedExpert || (editingExpert && editingExpert.name);
    if (!expertName) return;
    
    try {
      setSavingExpert(true);
      
      // Build category IDs from selections
      const categoryIds = [];
      const mainCategoryOnly = {};
      
      // Get all category IDs from ypn_categories
      const allCategories = [];
      Object.entries(availableCategories).forEach(([mainCat, subCats]) => {
        subCats.forEach(subCat => {
          allCategories.push({
            id: subCat.id,
            main_category: mainCat,
            sub_category: subCat.sub_category
          });
        });
      });
      
      // Process each selected main category
      selectedMainCategories.forEach(mainCat => {
        const subCatsForMain = selectedSubCategories.filter(sc => sc.startsWith(`${mainCat} >`));
        const subCats = availableCategories[mainCat] || [];
        
        if (subCatsForMain.length === 0) {
          // Main category only - add all sub-categories for this main category as "main category only"
          subCats.forEach(subCat => {
            if (!categoryIds.includes(subCat.id)) {
              categoryIds.push(subCat.id);
              mainCategoryOnly[subCat.id] = true;
            }
          });
        } else {
          // Some sub-categories selected - add only those, not as "main category only"
          subCatsForMain.forEach(key => {
            const [, subCatName] = key.split(' > ');
            const category = allCategories.find(c => c.main_category === mainCat && c.sub_category === subCatName);
            if (category && !categoryIds.includes(category.id)) {
              categoryIds.push(category.id);
              mainCategoryOnly[category.id] = false;
            }
          });
        }
      });
      
      // Also handle any sub-categories that might be selected without their main category being selected
      selectedSubCategories.forEach(key => {
        const [mainCat, subCatName] = key.split(' > ');
        if (!selectedMainCategories.includes(mainCat)) {
          // Sub-category selected but main category not in selectedMainCategories
          // This shouldn't happen, but handle it just in case
          const category = allCategories.find(c => c.main_category === mainCat && c.sub_category === subCatName);
          if (category && !categoryIds.includes(category.id)) {
            categoryIds.push(category.id);
            mainCategoryOnly[category.id] = false;
          }
        }
      });
      
      const response = await fetch(`${API_URL}/api/v6/experts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: expertName,
          category_ids: categoryIds,
          main_category_only: mainCategoryOnly
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        // Refresh experts list
        await fetchExperts();
        // Reset selection
        setSelectedExpert(null);
        setEditingExpert(null);
        setSelectedMainCategories([]);
        setSelectedSubCategories([]);
      } else {
        alert('Failed to save expert');
      }
    } catch (error) {
      console.error('Error saving expert:', error);
      alert('Error saving expert');
    } finally {
      setSavingExpert(false);
    }
  };

  // Filter documents
  useEffect(() => {
    let filtered = [...documents];

    // Filter by main category
    if (selectedMainCategory) {
      filtered = filtered.filter(doc => 
        doc.categories && doc.categories.some(cat => cat.main_category === selectedMainCategory)
      );
    }

    // Filter by sub category
    if (selectedSubCategory) {
      filtered = filtered.filter(doc => 
        doc.categories && doc.categories.some(cat => cat.sub_category === selectedSubCategory)
      );
    }

    // Filter by search term
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(doc => 
        doc.title.toLowerCase().includes(term) ||
        doc.issue?.toLowerCase().includes(term)
      );
    }

    setFilteredDocuments(filtered);
  }, [selectedMainCategory, selectedSubCategory, searchTerm, documents]);

  // Get unique main categories from all documents
  const getMainCategories = () => {
    const mainCats = new Set();
    documents.forEach(doc => {
      doc.categories?.forEach(cat => {
        mainCats.add(cat.main_category);
      });
    });
    return Array.from(mainCats).sort();
  };

  // Get sub categories for selected main category
  const getSubCategories = () => {
    if (!selectedMainCategory) return [];
    const subCats = new Set();
    documents.forEach(doc => {
      doc.categories?.forEach(cat => {
        if (cat.main_category === selectedMainCategory) {
          subCats.add(cat.sub_category);
        }
      });
    });
    return Array.from(subCats).sort();
  };

  // Open add category modal
  const handleAddCategory = (document) => {
    setSelectedDocument(document);
    setSelectedCategoriesToAdd([]);
    setShowAddCategoryModal(true);
  };

  // Add categories to document
  const handleSaveCategories = async () => {
    if (!selectedDocument || selectedCategoriesToAdd.length === 0) return;

    try {
      setAddingCategories(true);
      
      // Get category IDs from selected categories
      const categoryIds = selectedCategoriesToAdd.map(catKey => {
        const [main, sub] = catKey.split(' > ');
        // Find the category ID from available categories
        const mainCats = availableCategories[main] || [];
        const category = mainCats.find(c => c.sub_category === sub);
        return category?.id;
      }).filter(Boolean);

      if (categoryIds.length === 0) {
        alert('No valid categories selected');
        return;
      }

      // Use the add categories endpoint (doesn't replace existing)
      const response = await fetch(`${API_URL}/api/v6/documents/${selectedDocument.id}/categories/add`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(categoryIds)
      });

      if (response.ok) {
        await response.json();
        await fetchDocuments();
        setShowAddCategoryModal(false);
        setSelectedDocument(null);
        setSelectedCategoriesToAdd([]);
      } else {
        const error = await response.json();
        alert(`Error: ${error.detail || 'Failed to add categories'}`);
      }
    } catch (error) {
      console.error('Error adding categories:', error);
      alert('Failed to add categories');
    } finally {
      setAddingCategories(false);
    }
  };

  // Toggle category selection in modal
  const toggleCategorySelection = (mainCategory, subCategory) => {
    const key = `${mainCategory} > ${subCategory}`;
    setSelectedCategoriesToAdd(prev => {
      if (prev.includes(key)) {
        return prev.filter(k => k !== key);
      } else {
        return [...prev, key];
      }
    });
  };

  // Check if category is already assigned to document
  const isCategoryAssigned = (mainCategory, subCategory) => {
    if (!selectedDocument) return false;
    return selectedDocument.categories?.some(
      cat => cat.main_category === mainCategory && cat.sub_category === subCategory
    );
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Categories</h1>
        <p className="text-gray-600">Browse and manage article categories</p>
      </div>

      {/* Tabs */}
      <div className="mb-6 border-b border-gray-200">
        <nav className="flex space-x-8">
          <button
            onClick={() => setActiveTab('documents')}
            className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 ${
              activeTab === 'documents'
                ? 'border-purple-500 text-purple-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <FileTextIcon className="h-5 w-5" />
            <span>Documents</span>
          </button>
          <button
            onClick={() => setActiveTab('people')}
            className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 ${
              activeTab === 'people'
                ? 'border-purple-500 text-purple-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Users className="h-5 w-5" />
            <span>People</span>
          </button>
        </nav>
      </div>

      {activeTab === 'documents' ? (
        <>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <div className="flex items-center space-x-2 mb-4">
          <Filter className="h-5 w-5 text-gray-500" />
          <h2 className="text-lg font-semibold text-gray-900">Filters</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search documents..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
          </div>

          {/* Main Category Filter */}
          <select
            value={selectedMainCategory}
            onChange={(e) => {
              setSelectedMainCategory(e.target.value);
              setSelectedSubCategory(''); // Reset sub category when main changes
            }}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          >
            <option value="">All Main Categories</option>
            {getMainCategories().map(mainCat => (
              <option key={mainCat} value={mainCat}>{mainCat}</option>
            ))}
          </select>

          {/* Sub Category Filter */}
          <select
            value={selectedSubCategory}
            onChange={(e) => setSelectedSubCategory(e.target.value)}
            disabled={!selectedMainCategory}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
          >
            <option value="">All Sub Categories</option>
            {getSubCategories().map(subCat => (
              <option key={subCat} value={subCat}>{subCat}</option>
            ))}
          </select>
        </div>

        {/* Clear Filters */}
        {(selectedMainCategory || selectedSubCategory || searchTerm) && (
          <button
            onClick={() => {
              setSelectedMainCategory('');
              setSelectedSubCategory('');
              setSearchTerm('');
            }}
            className="mt-4 text-sm text-purple-600 hover:text-purple-800 flex items-center space-x-1"
          >
            <X className="h-4 w-4" />
            <span>Clear all filters</span>
          </button>
        )}
      </div>

      {/* Documents List */}
      {loading ? (
        <div className="flex justify-center items-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-purple-600" />
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex justify-between items-center">
              <h2 className="text-lg font-semibold text-gray-900">
                Documents ({filteredDocuments.length})
              </h2>
            </div>
          </div>

          <div className="divide-y divide-gray-200">
            {filteredDocuments.length === 0 ? (
              <div className="px-6 py-12 text-center text-gray-500">
                No documents found
              </div>
            ) : (
              filteredDocuments.map((doc) => (
                <div key={doc.id} className="px-6 py-4 hover:bg-gray-50 transition-colors">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-2">
                        <h3 className="text-lg font-medium text-gray-900">
                          {doc.title}
                        </h3>
                        {doc.storage_path && getPdfUrl(doc.storage_path) && (
                          <a
                            href={getPdfUrl(doc.storage_path)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center space-x-1 text-purple-600 hover:text-purple-800 transition-colors"
                            title="Open PDF"
                          >
                            <FileText className="h-4 w-4" />
                            <ExternalLink className="h-3 w-3" />
                          </a>
                        )}
                      </div>
                      <div className="flex items-center space-x-4 text-sm text-gray-500 mb-3">
                        {doc.issue && (
                          <span>Issue: {doc.issue}</span>
                        )}
                        {doc.year && (
                          <span>Year: {doc.year}</span>
                        )}
                      </div>
                      
                      {/* Category Lozenges */}
                      <div className="flex flex-wrap gap-2">
                        {doc.categories && doc.categories.length > 0 ? (
                          doc.categories.map((cat, index) => (
                            <span
                              key={`${doc.id}-${cat.category_id}-${index}`}
                              className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
                                cat.is_primary
                                  ? 'bg-purple-100 text-purple-800 border border-purple-200'
                                  : 'bg-gray-100 text-gray-800 border border-gray-200'
                              }`}
                            >
                              {cat.is_primary && <Star className="h-3 w-3 mr-1" />}
                              <span className="font-medium">{cat.main_category}</span>
                              <span className="mx-1">›</span>
                              <span>{cat.sub_category}</span>
                              {cat.relevance_score && (
                                <span className="ml-2 text-xs opacity-75">
                                  ({Math.round(cat.relevance_score * 100)}%)
                                </span>
                              )}
                            </span>
                          ))
                        ) : (
                          <span className="text-sm text-gray-400 italic">No categories assigned</span>
                        )}
                      </div>
                    </div>

                    {/* Add Category Button */}
                    <button
                      onClick={() => handleAddCategory(doc)}
                      className="ml-4 flex items-center space-x-2 px-3 py-2 text-sm font-medium text-purple-600 hover:text-purple-800 hover:bg-purple-50 rounded-lg transition-colors"
                    >
                      <Plus className="h-4 w-4" />
                      <span>Add Category</span>
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* Add Category Modal */}
      {showAddCategoryModal && selectedDocument && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[80vh] overflow-hidden flex flex-col">
            <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
              <h3 className="text-xl font-semibold text-gray-900">
                Add Categories to: {selectedDocument.title}
              </h3>
              <button
                onClick={() => {
                  setShowAddCategoryModal(false);
                  setSelectedDocument(null);
                  setSelectedCategoriesToAdd([]);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="px-6 py-4 overflow-y-auto flex-1">
              <div className="space-y-4">
                {Object.entries(availableCategories).map(([mainCategory, subCategories]) => (
                  <div key={mainCategory} className="border border-gray-200 rounded-lg p-4">
                    <h4 className="font-semibold text-gray-900 mb-2">{mainCategory}</h4>
                    <div className="flex flex-wrap gap-2">
                      {subCategories.map((subCat) => {
                        const key = `${mainCategory} > ${subCat.sub_category}`;
                        const isSelected = selectedCategoriesToAdd.includes(key);
                        const isAssigned = isCategoryAssigned(mainCategory, subCat.sub_category);
                        
                        return (
                          <button
                            key={subCat.id}
                            onClick={() => !isAssigned && toggleCategorySelection(mainCategory, subCat.sub_category)}
                            disabled={isAssigned}
                            className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                              isAssigned
                                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                : isSelected
                                ? 'bg-purple-600 text-white'
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                          >
                            {isAssigned && <CheckCircle className="h-3 w-3 inline mr-1" />}
                            {subCat.sub_category}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowAddCategoryModal(false);
                  setSelectedDocument(null);
                  setSelectedCategoriesToAdd([]);
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveCategories}
                disabled={selectedCategoriesToAdd.length === 0 || addingCategories}
                className="px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded-lg hover:bg-purple-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center space-x-2"
              >
                {addingCategories && <Loader2 className="h-4 w-4 animate-spin" />}
                <span>Add {selectedCategoriesToAdd.length} Categor{selectedCategoriesToAdd.length === 1 ? 'y' : 'ies'}</span>
              </button>
            </div>
          </div>
        </div>
      )}
        </>
      ) : (
        <>
          {/* People Tab */}
          {loadingExperts ? (
            <div className="flex justify-center items-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-purple-600" />
            </div>
          ) : (
            <div className="space-y-8">
              {/* Allocation Section */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                <div className="px-6 py-4 border-b border-gray-200">
                  <h2 className="text-lg font-semibold text-gray-900">Allocation</h2>
                  <p className="text-sm text-gray-500 mt-1">Select an expert and assign categories</p>
                </div>
                
                <div className="p-6 space-y-6">
                  {/* Row 1: Experts */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {editingExpert ? `Editing: ${editingExpert.name}` : 'Experts'}
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {experts.pending.map((expert) => (
                        <button
                          key={expert.id || expert.name}
                          type="button"
                          onClick={() => handleExpertSelect(expert.name)}
                          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                            selectedExpert === expert.name && !editingExpert
                              ? 'bg-purple-600 text-white'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                        >
                          {expert.name}
                        </button>
                      ))}
                      {experts.pending.length === 0 && !editingExpert && (
                        <p className="text-sm text-gray-500">No experts pending allocation</p>
                      )}
                    </div>
                  </div>

                  {/* Row 2: Main Categories (shown when expert selected or editing) */}
                  {(selectedExpert || editingExpert) && (
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <label className="block text-sm font-medium text-gray-700">Main Categories</label>
                        <button
                          type="button"
                          onClick={() => setShowAddCategoryForm(!showAddCategoryForm)}
                          className="px-3 py-1 text-sm text-purple-600 hover:text-purple-800 hover:bg-purple-50 rounded-lg transition-colors flex items-center space-x-1"
                        >
                          <Plus className="h-4 w-4" />
                          <span>Add New Category</span>
                        </button>
                      </div>
                      
                      {/* Add New Category Form */}
                      {showAddCategoryForm && (
                        <div className="mb-4 p-4 bg-purple-50 border border-purple-200 rounded-lg">
                          <h4 className="text-sm font-medium text-gray-900 mb-3">Create New Category</h4>
                          
                          <div className="mb-3">
                            <label className="block text-xs font-medium text-gray-700 mb-1">Main Category</label>
                            <input
                              type="text"
                              value={newMainCategory}
                              onChange={(e) => setNewMainCategory(e.target.value)}
                              placeholder="e.g., Legal & Compliance"
                              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                            />
                          </div>
                          
                          <div className="mb-3">
                            <label className="block text-xs font-medium text-gray-700 mb-1">Sub Categories</label>
                            {newSubCategories.map((subCat, index) => (
                              <div key={index} className="flex items-center space-x-2 mb-2">
                                <input
                                  type="text"
                                  value={subCat}
                                  onChange={(e) => {
                                    const updated = [...newSubCategories];
                                    updated[index] = e.target.value;
                                    setNewSubCategories(updated);
                                  }}
                                  placeholder="Sub-category name"
                                  className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                                />
                                {index === newSubCategories.length - 1 && (
                                  <button
                                    type="button"
                                    onClick={() => setNewSubCategories([...newSubCategories, ''])}
                                    className="px-3 py-2 text-purple-600 hover:text-purple-800 hover:bg-purple-100 rounded-lg transition-colors"
                                    title="Add another sub-category"
                                  >
                                    <Plus className="h-4 w-4" />
                                  </button>
                                )}
                                {newSubCategories.length > 1 && (
                                  <button
                                    type="button"
                                    onClick={() => {
                                      const updated = newSubCategories.filter((_, i) => i !== index);
                                      setNewSubCategories(updated);
                                    }}
                                    className="px-3 py-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition-colors"
                                    title="Remove sub-category"
                                  >
                                    <X className="h-4 w-4" />
                                  </button>
                                )}
                              </div>
                            ))}
                          </div>
                          
                          <div className="flex justify-end space-x-2">
                            <button
                              type="button"
                              onClick={() => {
                                setShowAddCategoryForm(false);
                                setNewMainCategory('');
                                setNewSubCategories(['']);
                              }}
                              className="px-4 py-2 text-sm text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors"
                            >
                              Cancel
                            </button>
                            <button
                              type="button"
                              onClick={handleSaveNewCategory}
                              disabled={savingCategory || !newMainCategory || newSubCategories.every(sc => !sc.trim())}
                              className="px-4 py-2 text-sm text-white bg-purple-600 rounded-lg hover:bg-purple-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center space-x-2"
                            >
                              {savingCategory && <Loader2 className="h-4 w-4 animate-spin" />}
                              <span>Save Category</span>
                            </button>
                          </div>
                        </div>
                      )}
                      
                      <div className="flex flex-wrap gap-2">
                        {Object.keys(availableCategories).map((mainCat) => (
                          <button
                            key={mainCat}
                            type="button"
                            onClick={() => handleMainCategoryToggle(mainCat)}
                            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                              selectedMainCategories.includes(mainCat)
                                ? 'bg-blue-600 text-white'
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                          >
                            {mainCat}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Row 3: Sub Categories (shown when main category selected) */}
                  {(selectedExpert || editingExpert) && selectedMainCategories.length > 0 && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Sub Categories</label>
                      {selectedMainCategories.map((mainCat) => {
                        const subCats = availableCategories[mainCat] || [];
                        const selectedSubCats = selectedSubCategories.filter(sc => sc.startsWith(`${mainCat} >`));
                        const allSelected = subCats.length > 0 && selectedSubCats.length === subCats.length;
                        
                        return (
                          <div key={mainCat} className="mb-4">
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-sm font-medium text-gray-700">{mainCat}:</span>
                              <button
                                type="button"
                                onClick={() => handleSelectAllSubCategories(mainCat)}
                                className="text-xs text-purple-600 hover:text-purple-800"
                              >
                                {allSelected ? 'Deselect All' : 'Select All'}
                              </button>
                            </div>
                            <div className="flex flex-wrap gap-2">
                              {subCats.map((subCat) => {
                                const key = `${mainCat} > ${subCat.sub_category}`;
                                const isSelected = selectedSubCategories.includes(key);
                                return (
                                  <button
                                    key={subCat.id}
                                    type="button"
                                    onClick={() => handleSubCategoryToggle(mainCat, subCat.sub_category)}
                                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                                      isSelected
                                        ? 'bg-green-600 text-white'
                                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                    }`}
                                  >
                                    {subCat.sub_category}
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Save/Cancel Buttons */}
                  {(selectedExpert || editingExpert) && (selectedMainCategories.length > 0 || selectedSubCategories.length > 0) && (
                    <div className="flex justify-end space-x-3">
                      {editingExpert && (
                        <button
                          onClick={handleCancelEdit}
                          disabled={savingExpert}
                          className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 disabled:bg-gray-100 disabled:cursor-not-allowed transition-colors"
                        >
                          Cancel
                        </button>
                      )}
                      <button
                        onClick={handleSaveExpert}
                        disabled={savingExpert}
                        className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center space-x-2"
                      >
                        {savingExpert && <Loader2 className="h-4 w-4 animate-spin" />}
                        <span>{editingExpert ? 'Update' : 'Save'}</span>
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Completed Section */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                <div className="px-6 py-4 border-b border-gray-200">
                  <h2 className="text-lg font-semibold text-gray-900">Completed</h2>
                  <p className="text-sm text-gray-500 mt-1">Experts with assigned categories</p>
                </div>
                
                <div className="p-6">
                  {experts.completed.length === 0 ? (
                    <p className="text-sm text-gray-500 text-center py-8">No experts completed yet</p>
                  ) : (
                    <div className="space-y-4">
                      {experts.completed.map((expert) => (
                        <div key={expert.id} className="border border-gray-200 rounded-lg p-4">
                          <div className="flex justify-between items-start mb-2">
                            <h3 className="font-semibold text-gray-900">{expert.name}</h3>
                            <button
                              type="button"
                              onClick={() => handleEditExpert(expert)}
                              disabled={editingExpert && editingExpert.id === expert.id}
                              className="px-3 py-1 text-sm text-purple-600 hover:text-purple-800 hover:bg-purple-50 rounded-lg transition-colors flex items-center space-x-1 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              <Edit2 className="h-4 w-4" />
                              <span>Edit</span>
                            </button>
                          </div>
                          {expert.categories && expert.categories.length > 0 ? (
                            <div className="flex flex-wrap gap-2">
                              {expert.categories.map((cat, idx) => (
                                <span
                                  key={idx}
                                  className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800"
                                >
                                  {cat.main_category}
                                  {!cat.is_main_category_only && cat.sub_category && (
                                    <span className="ml-1">→ {cat.sub_category}</span>
                                  )}
                                </span>
                              ))}
                            </div>
                          ) : (
                            <p className="text-sm text-gray-500">No categories assigned</p>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default CategoriesPage;

